import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Alert, Box, Button, Card, CardContent, Link, Stack, TextField, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { authReturnPath } from './NativeAccountPage';

export default function LoginForm() {
  const { login, authState, isAuthenticated } = useAuth();
  const location = useLocation();
  const next = authReturnPath(location.search, location.state);
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [continueHref, setContinueHref] = useState<string | null>(null);
  const nativeLogin = `/accounts/login/?${new URLSearchParams({ next })}`;
  useEffect(() => {
    if (isAuthenticated) window.location.assign(next);
  }, [isAuthenticated, next]);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (authState.isLoading) return;
    setContinueHref(null);
    const result = await login({ email, password });
    if (result.success) window.location.assign(next);
    else if (result.status === 401) {
      const mfa = result.data?.flows?.some((flow) => flow.id === 'mfa_authenticate' && flow.is_pending);
      setContinueHref(mfa ? `/accounts/2fa/authenticate/?${new URLSearchParams({ next })}` : nativeLogin);
    }
  };
  return (
    <Box sx={{ maxWidth: 480, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Card>
        <CardContent>
          <Stack spacing={3}>
            <Typography variant="h4" component="h1">
              Sign In
            </Typography>
            <Box component="form" onSubmit={submit}>
              <Stack spacing={2}>
                <TextField
                  label="Email"
                  type="email"
                  autoComplete="email"
                  required
                  fullWidth
                  value={email}
                  disabled={authState.isLoading}
                  onChange={(event) => setEmail(event.target.value)}
                />
                <TextField
                  label="Password"
                  type="password"
                  autoComplete="current-password"
                  required
                  fullWidth
                  value={password}
                  disabled={authState.isLoading}
                  onChange={(event) => setPassword(event.target.value)}
                />
                {authState.error && <Alert severity="error">{authState.error}</Alert>}
                {continueHref && (
                  <Alert severity="info">
                    Complete the remaining sign-in steps on the account page.{' '}
                    <Link href={continueHref}>Continue Sign-In</Link>
                  </Alert>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  disabled={authState.isLoading}
                  loading={authState.isLoading}
                  loadingPosition="start"
                >
                  {authState.isLoading ? 'Signing In…' : 'Sign In'}
                </Button>
              </Stack>
            </Box>
            <Button component="a" href={nativeLogin} variant="outlined">
              More Sign-In Options
            </Button>
            <Link href="/auth/password-reset">Forgot your password?</Link>
            <Link href={`/auth/signup?${new URLSearchParams({ next })}`}>Create an Account</Link>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
